import { useApiInstance } from 'api';
import { parseCookies } from 'nookies';
import qs from 'qs';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import FieldSet from '@/components/FieldSet/FieldSet';
import Label from '@/components/Form/Label/Label';
import {
  CheckEmail,
  CheckMinLength,
  CheckRequired,
  composeValidators,
  MapServerErrors,
  PlainCheckboxAdapter,
} from '@/components/FormAdapter/FormAdapter';
import Grid from '@/components/Grid/Grid';
import GridWrap from '@/components/Grid/GridWrap';
import Link from '@/components/Link/Link';
import Paper from '@/components/Paper/Paper';
import Typography from '@/components/Typography/Typography';
import Button from '@/components/v2/Button/Button';
import { InputFormField } from '@/components/v2/FinalForm/Fields/InputFormField';
import { showNotifyNew } from '@/models/Notify';
import { TRACKED_QUERY_PARAMS } from '@/pages/_app';

import EmptyCheckMail from '../Empty/EmptyCheckMail';
import LinkPersonalDataProcessing from '../Link/LinkPersonalDataProcessing';
import LinkPrivacyPolicy from '../Link/LinkPrivacyPolicy';

interface Values {
  full_name: string;
  cv_link: string;
  email: string;
  eula: boolean;
  invite_id: string;
}

interface Props {
  inviteId?: string;
  returnUrl?: string;
}

const RegisterCandidateForm: React.FC<Props> = ({ inviteId, returnUrl }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const api = useApiInstance();
  const [done] = useState<string | undefined>('');

  const register = async (values: Values) => {
    // Получаем трекинговые куки
    const cookies = parseCookies();
    const trackingParams: any = {};
    TRACKED_QUERY_PARAMS.forEach((paramName) => {
      if (cookies[`tr_${paramName}`]) {
        trackingParams[paramName] = cookies[`tr_${paramName}`];
      }
    });

    let result;
    try {
      result = await api.post('/candidate/register', { ...values, ...trackingParams });
    } catch (error) {
      result = error;
    }
    return result;
  };
  const onSubmit = async (values: Values) => {
    const result = await register(values);
    let errors;
    if (result.data) {
      // Используем обычный редирект, чтобы прошел SSR запрос и была установлена httpOnly cookie
      window.location.href = `/auth/token/${result.data.token}${
        returnUrl ? `?${qs.stringify({ return_url: returnUrl })}` : `?${qs.stringify({ return_url: '/my/cv/edit' })}`
      }`;
    } else if (result.response && result.response.status === 422) {
      errors = MapServerErrors(result.response.data.errors);
    } else {
      showNotifyNew(dispatch, {
        content: `Server error:
          ${result.statusCode ? result.statusCode : ''}
          ${result.message ? result.message : ''}`,
        type: 'error',
      });
    }
    return errors;
  };

  return (
    <>
      {!done ? (
        <Paper>
          <div className="geecko-registerform-candidate-bg" />
          <div className="geecko-registerform-block">
            <Typography variant="h1" component="h1" spacing={3}>
              <FormattedMessage id="app.register.developer.pagetitle" />
            </Typography>
            <Typography variant="body3" bold component="div" spacing={5}>
              {`${intl.formatMessage({ id: 'app.form.areyouemployer' })} `}
              <Link to="/register/company">{intl.formatMessage({ id: 'app.signup' })}</Link>
            </Typography>
            <Form
              onSubmit={onSubmit}
              initialValues={{
                full_name: '',
                email: '',
                cv_link: '',
                eula: false,
                invite_id: inviteId || '',
              }}
              render={({ handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit} style={{ margin: '30px 0 0' }} autoComplete="off">
                  <Grid container spacing={5}>
                    <Grid item xs={12} sm={6}>
                      <FieldSet spacing={5}>
                        <Label>{intl.formatMessage({ id: 'app.form.email.label' })}</Label>
                        <InputFormField
                          name="email"
                          type="email"
                          size="big"
                          validate={composeValidators(CheckRequired, CheckEmail)}
                        />
                      </FieldSet>
                      <FieldSet spacing={5}>
                        <Label>{intl.formatMessage({ id: 'app.form.password.requirements' })}</Label>
                        <InputFormField
                          name="password"
                          type="password"
                          size="big"
                          validate={composeValidators(CheckRequired, CheckMinLength(6))}
                          autoComplete="new-password"
                        />
                      </FieldSet>
                      <FieldSet spacing={2}>
                        <Field<boolean>
                          name="eula"
                          type="checkbox"
                          component={PlainCheckboxAdapter}
                          validate={CheckRequired}
                          content={
                            <>
                              {intl.formatMessage({ id: 'app.form.agreement.familiarized' })}
                              <LinkPersonalDataProcessing
                                title={intl.formatMessage({ id: 'app.form.agreement.argee' })}
                              />
                              {` ${intl.formatMessage({ id: 'app.form.agreement.with' })} `}
                              <LinkPrivacyPolicy
                                title={intl.formatMessage({ id: 'app.form.agreement.privacypolicy' })}
                              />
                            </>
                          }
                        />
                      </FieldSet>
                      <FieldSet spacing={5}>
                        <Field<boolean>
                          name="terms"
                          type="checkbox"
                          component={PlainCheckboxAdapter}
                          validate={CheckRequired}
                          content={
                            <>
                              {`${intl.formatMessage({ id: 'app.form.agreement.accept' })} `}
                              <Link to="/terms-of-use" target="_blank">
                                {intl.formatMessage({ id: 'app.docs.useragreement' })}
                              </Link>
                            </>
                          }
                        />
                      </FieldSet>
                      <GridWrap>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Button size="lg" type="submit" isWide disabled={submitting}>
                              <FormattedMessage id="app.register" />
                            </Button>
                          </Grid>
                          <Grid item xs={12} sm={6} />
                        </Grid>
                      </GridWrap>
                    </Grid>
                    <Grid item xs={12} sm={6} />
                  </Grid>
                </form>
              )}
            />
          </div>
        </Paper>
      ) : (
        <EmptyCheckMail text={intl.formatMessage({ id: 'app.register.success.message' }, { email: done })} />
      )}
    </>
  );
};

export default RegisterCandidateForm;
